/**
 * E07-F04-T11 — the per-company CHECK STATE of a long-list row.
 *
 * A research_companies row used to have only two visible states, driven by the
 * `needs_human` boolean: "Citation-verified" (false) and "Needs human" (true). A row
 * created by ZoomInfo discovery is written with needs_human=false and NOTHING has
 * checked it yet — so an unchecked company looked verified (green Evidence banner, no
 * Review highlight, missing from the needs-human counter and the Publish warning).
 *
 * The third state is DERIVED (no column, no migration — dev data is read as-is):
 *
 *   needs_human  — the row carries needs_human=true (a check flagged it, or it was
 *                  added manually). Highest priority, unchanged legacy meaning.
 *   verified     — a check actually ran: `stage` reached 'verified'/'scored' (the
 *                  Review Verify button, Request-more-info, the legacy pipeline or the
 *                  manual AI check all advance the stage) or the AI check finished
 *                  (`ai_run_status='scored'`), and it did not flag the row.
 *   not_checked  — neither of the above: the row is raw discovery data (stage
 *                  discovered/deduped/..., or an AI run that FAILED — a failed run
 *                  confirmed nothing).
 *
 * Compatibility: a caller that does not know the row's `stage` / `aiRunStatus`
 * (both undefined — an old projection or fixture that never selected them) gets the
 * legacy two-state answer, so nothing that predates this field silently flips.
 */
import { z } from 'zod';
export const CHECK_STATES = ['not_checked', 'needs_human', 'verified'];
export const checkStateSchema = z.enum(CHECK_STATES);
/** Pipeline stages at/after which a row's criteria have been checked. */
export const CHECKED_STAGES = ['verified', 'scored'];
/** Derive the check state of one row (see the module header for the rule). */
export function deriveCheckState(row) {
    if (row.needsHuman)
        return 'needs_human';
    // Unknown provenance (neither field supplied) → legacy two-state behaviour.
    if (row.stage === undefined && row.aiRunStatus === undefined)
        return 'verified';
    if (row.aiRunStatus === 'scored')
        return 'verified';
    if (row.stage != null && CHECKED_STAGES.includes(row.stage))
        return 'verified';
    return 'not_checked';
}
/** Short UI label per state (the neutral Review/Evidence marker reads "Not checked"). */
export const CHECK_STATE_LABEL = {
    not_checked: 'Not checked',
    needs_human: 'Needs human',
    verified: 'Verified',
};
//# sourceMappingURL=research-check-state.js.map